/**
 * Admin Dashboard
 *
 * Entry page for admins to access management sections: Users, Templates,
 * Stock Videos, Enterprises, and Tools. Redirects non-admins.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import Footer from '../../../components/shared/Footer';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { Shield, LayoutGrid, Users, Building2, Wrench, Video } from 'lucide-react';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  
  // Check if user is the admin using the same pattern as other admin pages
  const isAdmin = isUserAdmin(user);

  // Check if user is an admin, if not, redirect to home page
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      }
      setIsLoading(false);
    } else if (user === null) {
      // No user, redirect to login
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  // Admin categories
  const adminCategories = [
    {
      title: 'Users',
      description: 'Manage user accounts and free users',
      icon: Users,
      path: '/app/private/admin/users',
      color: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300',
    },
    {
      title: 'Templates',
      description: 'Manage templates, create new ones, and review custom submissions',
      icon: LayoutGrid,
      path: '/app/private/admin/templates',
      color: 'bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-300',
    },
    {
      title: 'Stock Videos',
      description: 'Create and manage stock videos for campaigns and templates',
      icon: Video,
      path: '/app/private/admin/stock-videos',
      color: 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300',
    },
    {
      title: 'Enterprises',
      description: 'Create and manage enterprise accounts and configurations',
      icon: Building2,
      path: '/app/private/admin/enterprises',
      color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
    },
    {
      title: 'Tools',
      description: 'Manage tags, view analytics, and access other administrative tools',
      icon: Wrench,
      path: '/app/private/admin/tools',
      color: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300',
    }
  ];

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading admin dashboard..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Redirect happens in useEffect
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto pb-20">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Shield className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Admin Dashboard</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Manage your application's content and settings from this centralized dashboard.
        </p>
      </div>

      {/* Admin categories grid */}
      <div className="flex flex-col gap-4">
        {adminCategories.map((category, index) => (
          <button
            key={category.path || `admin-category-${index}`}
            onClick={category.path ? () => navigate(category.path) : () => {}}
            className="flex items-center w-full p-6 bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            <div className={`p-2 rounded-lg ${category.color} mr-4`}>
              <category.icon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {category.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {category.description}
              </p>
            </div>
            <div className="w-6 flex items-center justify-center h-5">
              <svg className="h-4 w-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        ))}
      </div>

      <Footer />
    </div>
  );
} 